import styled from "styled-components";

// Design tokens (Figma 19320:192458): border Grayscale/2 (#DEDEDE), hover
// Grayscale/3 (#C7C8C9), SmartSuite Primary (#3A86FF) for the active border,
// Text-Primary (#2E3538) / Text-Secondary placeholder (#878B92). Compact 36px
// input matching the neighbouring operator/value inputs. The "+" itself is the
// shared `plus-circle` Icon (same as the right-side value input), not a bespoke
// circle, so both sides render identically.
const BORDER = "#DEDEDE";
const BORDER_HOVER = "#C7C8C9";
const PRIMARY_BLUE = "#3A86FF";
const TEXT_PRIMARY = "#2E3538";
const TEXT_SECONDARY = "#878B92";

export const StyledLeftControl = styled.div<{ $open: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  height: 36px;
  box-sizing: border-box;
  padding: 0 8px 0 12px;
  background-color: #ffffff;
  border: 1px solid ${({ $open }) => ($open ? PRIMARY_BLUE : BORDER)};
  /* $radius-basic (5px) -- matches the neighbouring .react-input-group inputs. */
  border-radius: 5px;
  cursor: pointer;
  transition: border-color 120ms ease, box-shadow 120ms ease;
  ${({ $open }) => $open && "box-shadow: 0 0 0 2px rgba(58, 134, 255, 0.15);"}

  &:hover {
    border-color: ${({ $open }) => ($open ? PRIMARY_BLUE : BORDER_HOVER)};
  }

  &:focus-visible {
    outline: none;
    border-color: ${PRIMARY_BLUE};
    box-shadow: 0 0 0 2px rgba(58, 134, 255, 0.15);
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

/* Constrains the selected-field pill (and its Tooltip wrapper) to the control's
   free space so a long field name ellipsizes inside the pill instead of
   pushing the "+" affordance out of the 36px control. */
export const StyledPillWrapper = styled.span`
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  animation: condition-left-pill-in 150ms ease-out;

  /* FieldTypePill wraps its target in Tooltip, which renders an OUTER
     .tooltip.tooltip--inline-block (display: inline-block) around the
     .tooltip__outside node. That outer wrapper is the first flex item inside
     this wrapper, so the whole min-width:0 chain has to start there -- an
     inline-block flex item keeps min-width:auto and refuses to shrink,
     breaking the ellipsis before it ever reaches the pill. Force it to a
     shrinkable flex box. */
  .tooltip {
    display: flex;
    min-width: 0;
    max-width: 100%;
  }

  .tooltip__outside {
    display: inline-flex;
    min-width: 0;
    max-width: 100%;
  }

  .field-type-pill {
    max-width: 100%;
    /* Completes the min-width:0 flex chain (wrapper -> tooltip ->
       tooltip__outside -> pill) so the pill can shrink and the inner Text's
       built-in ellipsis engages, instead of the long field name spilling past
       the control. */
    min-width: 0;
  }

  @keyframes condition-left-pill-in {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const StyledFieldLabel = styled.span<{ $placeholder: boolean }>`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  line-height: 18px;
  color: ${({ $placeholder }) =>
    $placeholder ? TEXT_SECONDARY : TEXT_PRIMARY};
`;
